import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, BadgeCheck, GraduationCap, Heart, Users } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { IMAGES } from "@/data/site";

const OPEN_ROLES = [
  { title: "Certified Nursing Assistant (CNA)", location: "Atlanta Metro · GA", type: "Full-time", division: "Home Health" },
  { title: "Personal Care Assistant (PCA)", location: "Statewide · GA", type: "Full / Part-time", division: "Home Health" },
  { title: "NEMT Driver", location: "Multiple Counties · GA", type: "Full-time", division: "Transportation" },
  { title: "Dispatcher / Scheduler", location: "Atlanta · GA", type: "Full-time", division: "Transportation" },
  { title: "Physical Therapist (PT)", location: "Hybrid · GA", type: "PRN", division: "Therapy" },
  { title: "Compliance & Quality Coordinator", location: "Atlanta · GA", type: "Full-time", division: "Consulting" },
];

const PERKS = [
  { icon: Heart, title: "Healthcare benefits", body: "Medical, dental, vision for full-time team members." },
  { icon: GraduationCap, title: "Tuition support", body: "CNA, CPR, and continuing-ed reimbursement through the Academy." },
  { icon: BadgeCheck, title: "Career ladders", body: "Defined growth from frontline care into clinical leadership." },
  { icon: Users, title: "Community first", body: "Work close to home, in the communities you already know." },
];

export default function Careers() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", position: OPEN_ROLES[0].title,
    location: "", years_experience: "", cover_letter: "", resume_link: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/careers/apply", form);
      toast.success("Application received — our team will reach out shortly.");
      setForm({
        full_name: "", email: "", phone: "", position: OPEN_ROLES[0].title,
        location: "", years_experience: "", cover_letter: "", resume_link: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-careers">
      <PageHeader
        overline="Careers at 1TEH"
        title="Join a team that"
        italic="actually shows up."
        description="We're building Georgia's next great healthcare enterprise — and we hire the people who make that possible: caregivers, drivers, therapists, schedulers, leaders. Roles below."
        image={IMAGES.careersHero}
      />

      {/* Perks */}
      <section className="py-20 bg-teh-bg">
        <div className="teh-container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PERKS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="p-7 bg-white border border-teh-navy/10 rounded-2xl">
                  <Icon className="w-8 h-8 text-teh-green mb-5" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl text-teh-deep">{p.title}</h3>
                  <p className="mt-2 text-sm text-teh-muted">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Open roles */}
      <section className="py-20 bg-teh-surfaceAlt">
        <div className="teh-container">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="teh-overline">Open Roles</p>
              <h2 className="font-serif text-4xl md:text-5xl mt-3 text-teh-deep">
                We&rsquo;re hiring across <span className="italic text-teh-green">every division.</span>
              </h2>
            </div>
            <a href="#apply" className="text-sm font-medium text-teh-navy hover:text-teh-green inline-flex items-center gap-2">
              Don&rsquo;t see your role? Apply anyway <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="divide-y divide-teh-navy/10 bg-white rounded-2xl border border-teh-navy/10">
            {OPEN_ROLES.map((r) => (
              <div
                key={r.title}
                data-testid={`role-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 md:px-10 py-7 hover:bg-teh-bg/60 transition cursor-pointer"
                onClick={() => {
                  setForm((prev) => ({ ...prev, position: r.title }));
                  document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teh-green">
                    {r.division}
                  </p>
                  <h3 className="font-serif text-2xl text-teh-deep mt-2 group-hover:text-teh-navy">
                    {r.title}
                  </h3>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-teh-muted">
                  <span>{r.location}</span>
                  <span className="w-1 h-1 rounded-full bg-teh-muted/40" />
                  <span>{r.type}</span>
                  <ArrowRight className="w-5 h-5 text-teh-navy group-hover:translate-x-1 transition" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply" className="py-24 bg-teh-bg">
        <div className="teh-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="teh-overline">Apply Now</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-teh-deep leading-tight">
              Tell us about
              <br /><span className="italic text-teh-green">yourself.</span>
            </h2>
            <p className="mt-6 text-teh-muted">
              Submit your details and a member of our talent team will be in touch within
              two business days.
            </p>
          </div>

          <form
            onSubmit={submit}
            data-testid="careers-form"
            className="lg:col-span-8 bg-white border border-teh-navy/10 rounded-2xl p-8 md:p-10 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full name" required>
                <input data-testid="careers-name" required value={form.full_name} onChange={update("full_name")} className={inputCls} />
              </Field>
              <Field label="Email" required>
                <input type="email" data-testid="careers-email" required value={form.email} onChange={update("email")} className={inputCls} />
              </Field>
              <Field label="Phone" required>
                <input data-testid="careers-phone" required value={form.phone} onChange={update("phone")} className={inputCls} />
              </Field>
              <Field label="Position">
                <select data-testid="careers-position" value={form.position} onChange={update("position")} className={inputCls}>
                  {OPEN_ROLES.map((r) => <option key={r.title}>{r.title}</option>)}
                  <option value="General Application">General Application</option>
                </select>
              </Field>
              <Field label="County / location">
                <input data-testid="careers-location" value={form.location} onChange={update("location")} className={inputCls} placeholder="e.g. Fulton County" />
              </Field>
              <Field label="Years of experience">
                <input data-testid="careers-experience" value={form.years_experience} onChange={update("years_experience")} className={inputCls} placeholder="e.g. 3" />
              </Field>
            </div>
            <Field label="Resume link (Google Drive, LinkedIn, Dropbox)">
              <input data-testid="careers-resume" value={form.resume_link} onChange={update("resume_link")} className={inputCls} placeholder="https://..." />
            </Field>
            <Field label="Cover letter / why 1st Touch?">
              <textarea data-testid="careers-cover" rows={5} value={form.cover_letter} onChange={update("cover_letter")} className={inputCls} />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              data-testid="careers-submit"
              className="inline-flex items-center gap-2 px-7 py-4 bg-teh-navy text-white text-sm font-medium rounded-full hover:bg-teh-navyDark hover:gap-3 transition disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit application"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full px-4 py-3 bg-teh-bg border border-teh-navy/15 rounded-lg text-teh-deep placeholder:text-teh-muted/60 focus:outline-none focus:border-teh-green focus:ring-2 focus:ring-teh-green/15 transition";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-teh-deep mb-2">
        {label} {required && <span className="text-teh-green">*</span>}
      </span>
      {children}
    </label>
  );
}
